export type Lang = 'en' | 'ar';

/** Western digits in both languages (DESIGN_SYSTEM.md §8): Arabic is ar-EG with the latn numbering system. */
export const LOCALES: Record<Lang, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

/** First-strong isolate … pop: keeps a plate, an IMEI or "64 km/h" in one piece inside Arabic. */
export const isolate = (s: string) => `⁨${s}⁩`;

/** The separator. Overpass draws "·" with zero width — always "•" (DESIGN_SYSTEM.md §3.3). */
export const SEPARATOR = ' • ';

export interface Formatters {
  locale: string;
  number(value: number | null | undefined, maxFractionDigits?: number): string;
  speed(kmh: number | null | undefined): string;
  distance(km: number | null | undefined): string;
  /** a length in metres: "500 m", "1.25 km" (a geofence radius) */
  meters(m: number | null | undefined): string;
  /** "1 hr 5 mins" / "1 س 5 د" — from minutes */
  duration(minutes: number | null | undefined): string;
  /** a UTC-fields wall-clock Date (from @vlm/api wallClock) → "20 Oct 2026, 18:59" */
  dateTime(wall: Date | null | undefined, withSeconds?: boolean): string;
  date(wall: Date | null | undefined): string;
  time(wall: Date | null | undefined, withSeconds?: boolean): string;
  /** a real instant → "12 sec ago" / "قبل 12 ثانية" */
  relative(instant: Date | null | undefined, now?: number): string;
  /** "21.42250, 39.82620" — always LTR */
  coordinates(lat: number | null | undefined, lon: number | null | undefined): string;
  /** an amount with its currency's ISO code and two decimals, "SAR 1,150.00" — never summed across currencies */
  money(amount: number | null | undefined, currency: string): string;
  /** a calendar day, "2026-10-20" → "20 Oct 2026" — billing days, no clock and no zone */
  day(day: string | null | undefined): string;
}

const EMPTY = '—';

export function createFormatters(lang: Lang): Formatters {
  const locale = LOCALES[lang];
  const cache = new Map<string, Intl.NumberFormat>();
  const nf = (key: string, options: Intl.NumberFormatOptions) => {
    let f = cache.get(key);
    if (!f) {
      f = new Intl.NumberFormat(locale, options);
      cache.set(key, f);
    }
    return f;
  };
  const dtf = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', hourCycle: 'h23', ...options });
  const dateTimeF = dtf({ day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const dateTimeSecF = dtf({ day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateF = dtf({ day: 'numeric', month: 'short', year: 'numeric' });
  const timeF = dtf({ hour: '2-digit', minute: '2-digit' });
  const timeSecF = dtf({ hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });

  const unit = (u: string, value: number, digits: number) =>
    isolate(nf(`${u}:${digits}`, { style: 'unit', unit: u, unitDisplay: 'short', maximumFractionDigits: digits }).format(value));

  return {
    locale,
    number: (value, digits = 2) => (value === null || value === undefined ? EMPTY : nf(`n:${digits}`, { maximumFractionDigits: digits }).format(value)),
    speed: (kmh) => (kmh === null || kmh === undefined ? EMPTY : unit('kilometer-per-hour', kmh, 0)),
    distance: (km) => (km === null || km === undefined ? EMPTY : unit('kilometer', km, km < 10 ? 2 : 1)),
    meters: (m) => (m === null || m === undefined ? EMPTY : m < 1000 ? unit('meter', m, 0) : unit('kilometer', m / 1000, 2)),
    duration(minutes) {
      if (minutes === null || minutes === undefined) return EMPTY;
      const total = Math.max(0, Math.round(minutes));
      const h = Math.floor(total / 60);
      const m = total % 60;
      if (h === 0) return unit('minute', m, 0);
      return m === 0 ? unit('hour', h, 0) : `${unit('hour', h, 0)} ${unit('minute', m, 0)}`;
    },
    dateTime: (wall, withSeconds = false) => (wall ? (withSeconds ? dateTimeSecF : dateTimeF).format(wall) : EMPTY),
    date: (wall) => (wall ? dateF.format(wall) : EMPTY),
    time: (wall, withSeconds = false) => (wall ? (withSeconds ? timeSecF : timeF).format(wall) : EMPTY),
    relative(instant, now = Date.now()) {
      if (!instant) return EMPTY;
      const seconds = Math.round((instant.getTime() - now) / 1000);
      const abs = Math.abs(seconds);
      if (abs < 60) return rtf.format(seconds, 'second');
      if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
      if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
      return rtf.format(Math.round(seconds / 86400), 'day');
    },
    coordinates: (lat, lon) => (lat === null || lat === undefined || lon === null || lon === undefined ? EMPTY : isolate(`${lat.toFixed(5)}, ${lon.toFixed(5)}`)),
    money: (amount, currency) =>
      amount === null || amount === undefined
        ? EMPTY
        : isolate(nf(`money:${currency}`, { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)),
    day(day) {
      const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
      return m ? dateF.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))) : EMPTY;
    },
  };
}
